// Centralised per-route SEO metadata for FCA Reports Australia.
// Each entry holds the exact title / description / keywords agreed with the SEO contractor.
// Pages read from this map via getPageSEO(path). For pages NOT in this map,
// the rendering page provides its own title/description and the SEO component falls back
// to the global default keyword list.
//
// NDIS Registered Provider integration (Phase 2, Feb 2026):
//   "Registered NDIS Provider" is added as a SECONDARY trust modifier — always
//   after the primary FCA + location keyword. FCA and location remain the
//   dominant search intent for every page. Wording is varied per page to avoid
//   templated duplication.

export const DEFAULT_KEYWORDS = [
  "FCA Reports Australia",
  "Functional Capacity Assessment",
  "NDIS Functional Capacity Assessment",
  "NDIS FCA",
  "Occupational Therapist FCA",
  "OT FCA",
  "Occupational Therapy Functional Capacity Assessment",
  "FCA Occupational Therapist",
  "NDIS Assessment Australia",
  "Registered NDIS Provider",
];

// Keyed by canonical path (no trailing slash except "/")
export const PAGE_SEO = {
  "/resources/sample-fca-report": {
    title: "Sample FCA Report | De-Identified Functional Capacity Assessment Example",
    description:
      "Download a de-identified Functional Capacity Assessment sample report and review the quality, structure and clinical reasoning behind FCA Reports Australia's FCA reports.",
    keywords: [
      "sample FCA report",
      "functional capacity assessment example",
      "de-identified FCA report",
      "NDIS FCA report sample",
      "functional capacity assessment template",
      "support coordinator FCA report",
      "independent FCA reports",
      "NDIS functional assessment",
    ],
  },
  "/": {
    title: "NDIS Functional Capacity Assessments Australia | Registered NDIS Provider",
    description:
      "Registered NDIS Provider delivering Occupational Therapist-led Functional Capacity Assessments Australia-wide. reports delivered within five business days of the final assessment interview. NDIA-managed participants welcome.",
    keywords: [
      "FCA Reports Australia",
      "Functional Capacity Assessment Australia",
      "NDIS FCA",
      "NDIS Functional Capacity Assessment",
      "Occupational Therapist FCA",
      "OT FCA",
      "Occupational Therapy Functional Capacity Assessment",
      "Registered NDIS Provider",
    ],
  },
  "/book-an-appointment": {
    title: "Request a Free Call | Registered NDIS Provider",
    description:
      "Request a free call for your NDIS Functional Capacity Assessment with FCA Reports Australia — a Registered NDIS Provider. Experienced AHPRA-registered Occupational Therapists, fast Australia-wide turnaround. NDIA-managed participants welcome.",
    keywords: [
      "NDIS Functional Capacity Assessment Booking",
      "FCA Appointment",
      "FCA Reports Australia",
      "Registered NDIS Provider",
    ],
  },
  "/services/sil-assessments": {
    title: "NDIS SIL Assessment Services Australia | FCA Reports Australia",
    description:
      "Independent NDIS SIL Assessment services Australia-wide, delivered by a Registered NDIS Provider. Supported Independent Living reports by experienced AHPRA-registered Occupational Therapists with fast turnaround.",
    keywords: [
      "NDIS Assessment Services Australia",
      "SIL Assessments",
      "Supported Independent Living Assessment",
      "Registered NDIS Provider",
    ],
  },
  "/services/sda-assessments": {
    title: "Best NDIS SDA Assessment Service Australia | FCA Reports Australia",
    description:
      "Looking for trusted SDA assessment services in Australia? FCA Reports Australia — a Registered NDIS Provider — delivers comprehensive Specialist Disability Accommodation assessments by AHPRA-registered Occupational Therapists.",
    keywords: [
      "NDIS SDA Assessment Australia",
      "SDA Assessments",
      "Specialist Disability Accommodation Assessment",
      "Registered NDIS Provider",
    ],
  },
  "/services/functional-capacity-assessment": {
    title: "NDIS Functional Capacity Assessment Australia | FCA Reports",
    description:
      "FCA Reports Australia provides trusted NDIS Functional Capacity Assessments completed by experienced AHPRA-registered Occupational Therapists. Registered NDIS Provider — NDIA-Managed participants welcome.",
    keywords: [
      "NDIS Functional Capacity Report",
      "Functional Capacity Assessment Australia",
      "Occupational Therapist FCA",
      "Registered NDIS Provider",
    ],
  },
  "/locations/sydney": {
    title: "NDIS Functional Capacity Assessment Sydney | FCA Reports",
    description:
      "Arrange a trusted NDIS Functional Capacity Assessment in Sydney with FCA Reports Australia. Experienced AHPRA-registered Occupational Therapists, fast Australia-wide service. Registered NDIS Provider serving Greater Sydney and NSW.",
    keywords: [
      "NDIS Functional Capacity Assessment Sydney",
      "FCA Sydney",
      "Functional Capacity Report Sydney",
      "Registered NDIS Provider Sydney",
    ],
  },
  "/locations/perth": {
    title: "NDIS Functional Capacity Assessment Perth | FCA Reports",
    description:
      "Get a professional NDIS Functional Capacity Assessment in Perth with FCA Reports Australia. Experienced AHPRA-registered Occupational Therapists, fast report turnaround. Registered NDIS Provider across WA.",
    keywords: [
      "Functional Capacity Report Perth",
      "NDIS FCA Perth",
      "Functional Capacity Assessment Perth",
      "Registered NDIS Provider Perth",
    ],
  },
  "/locations/brisbane": {
    title: "NDIS Functional Capacity Assessment Brisbane | FCA Reports",
    description:
      "FCA Reports Australia delivers trusted NDIS Functional Capacity Assessments in Brisbane. Experienced AHPRA-registered Occupational Therapists, fast Australia-wide turnaround. Registered NDIS Provider serving South East Queensland.",
    keywords: [
      "NDIS Functional Capacity Assessment Brisbane",
      "FCA Brisbane",
      "Functional Capacity Report Brisbane",
      "Registered NDIS Provider Brisbane",
    ],
  },
  "/locations/adelaide": {
    title: "NDIS Functional Capacity Assessment Adelaide | FCA Reports",
    description:
      "Arrange a reliable NDIS Functional Capacity Assessment in Adelaide with FCA Reports Australia. Experienced AHPRA-registered Occupational Therapists, fast report turnaround. Registered NDIS Provider serving South Australia.",
    keywords: [
      "Functional Capacity Report Adelaide",
      "NDIS FCA Adelaide",
      "Functional Capacity Assessment Adelaide",
      "Registered NDIS Provider Adelaide",
    ],
  },
  "/locations/melbourne": {
    title: "NDIS Functional Capacity Assessment Melbourne | FCA Reports",
    description:
      "FCA Reports Australia provides trusted NDIS Functional Capacity Assessments in Melbourne. Experienced AHPRA-registered Occupational Therapists with fast turnaround. Registered NDIS Provider serving Greater Melbourne and Victoria.",
    keywords: [
      "Functional Capacity Assessment Melbourne",
      "FCA Melbourne",
      "NDIS Functional Capacity Report Melbourne",
      "Registered NDIS Provider Melbourne",
    ],
  },
};

// Normalise + lookup
export function getPageSEO(path) {
  if (!path) return null;
  const normalised = path.length > 1 ? path.replace(/\/+$/, "") : "/";
  return PAGE_SEO[normalised] || null;
}
